
// Centralized error handling utility
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: Array<{ error: Error; context: string; timestamp: Date }> = [];

  private constructor() {}

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  public logError(error: Error, context: string = 'Unknown'): void {
    const errorEntry = {
      error,
      context,
      timestamp: new Date()
    };

    this.errorQueue.push(errorEntry);
    
    // Keep only last 100 errors
    if (this.errorQueue.length > 100) {
      this.errorQueue.shift();
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error(`[${context}]`, error);
    }

    // In production, you would send to monitoring service
    this.sendToMonitoring(errorEntry);
  }

  private sendToMonitoring(errorEntry: any): void {
    // Implement monitoring service integration
    // Example: Sentry, LogRocket, etc.
  }

  public getRecentErrors(): Array<{ error: Error; context: string; timestamp: Date }> {
    return [...this.errorQueue];
  }

  public clearErrors(): void {
    this.errorQueue = [];
  }
}

// Global error boundary hook
export const useErrorHandler = () => {
  const errorHandler = ErrorHandler.getInstance();

  const handleError = (error: Error, context?: string) => {
    errorHandler.logError(error, context);
  };

  return { handleError, errorHandler };
};

// Async error wrapper
export const withErrorHandling = async <T>(
  fn: () => Promise<T>,
  context: string = 'AsyncOperation'
): Promise<T | null> => {
  try {
    return await fn();
  } catch (error) {
    ErrorHandler.getInstance().logError(error as Error, context);
    return null;
  }
};
